import axios from "axios"

const config = window.__APP_CONFIG__ ?? {}

const api = axios.create({
  baseURL: config.API_BASE_URL,
})

// ── Request interceptor: attach JWT ───────────────────────────────────────
api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("access")
  if (token) cfg.headers.Authorization = `Bearer ${token}`
  return cfg
})

// ── Response interceptor: auto-refresh on 401 ────────────────────────────
let isRefreshing   = false
let refreshQueue: Array<(token: string | null) => void> = []

function processQueue(token: string | null) {
  refreshQueue.forEach(cb => cb(token))
  refreshQueue = []
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config

    // Only attempt refresh once per request; skip if it's the refresh call itself.
    if (
      error.response?.status !== 401 ||
      original._retry ||
      original.url?.includes("auth/token/refresh/")
    ) {
      return Promise.reject(error)
    }

    if (isRefreshing) {
      // Queue callers while a refresh is already in flight.
      return new Promise((resolve, reject) => {
        refreshQueue.push((token) => {
          if (token) {
            original.headers.Authorization = `Bearer ${token}`
            resolve(api(original))
          } else {
            reject(error)
          }
        })
      })
    }

    original._retry  = true
    isRefreshing     = true

    const refresh = localStorage.getItem("refresh")
    if (!refresh) {
      isRefreshing = false
      processQueue(null)
      // No refresh token — redirect to login
      window.location.href = "/login"
      return Promise.reject(error)
    }

    try {
      const res = await axios.post(
        `${config.API_BASE_URL}auth/token/refresh/`,
        { refresh },
      )
      const newAccess = res.data.access
      localStorage.setItem("access", newAccess)
      if (res.data.refresh) localStorage.setItem("refresh", res.data.refresh)
      api.defaults.headers.common.Authorization = `Bearer ${newAccess}`
      processQueue(newAccess)
      original.headers.Authorization = `Bearer ${newAccess}`
      return api(original)
    } catch (refreshError) {
      processQueue(null)
      localStorage.removeItem("access")
      localStorage.removeItem("refresh")
      window.location.href = "/login"
      return Promise.reject(refreshError)
    } finally {
      isRefreshing = false
    }
  },
)

export default api
